import type { QueryClient } from '@tanstack/react-query'
import type { InboundForwarding, InboundMessage } from 'types/inbound'

/** Keep the card overview and handler form in sync after a save or archive. */
export async function cacheSavedInboundForwarding(client: QueryClient, forwarding: InboundForwarding) {
  const listKey = ['boombox-api', '/api/inbound-forwardings']
  const detailKey = ['boombox-api', `/api/inbound-forwardings/${encodeURIComponent(forwarding.id)}`]
  await Promise.all([client.cancelQueries({ queryKey: listKey }), client.cancelQueries({ queryKey: detailKey })])
  client.setQueryData(detailKey, forwarding)
  client.setQueryData<InboundForwarding[]>(listKey, (rows) => {
    if (!rows) {
      return rows
    }
    return rows.some((row) => row.id === forwarding.id)
      ? rows.map((row) => (row.id === forwarding.id ? forwarding : row))
      : [forwarding, ...rows]
  })
  await client.invalidateQueries({ queryKey: listKey })
}

/** Replace terminal state immediately and prevent an older GET from restoring it. */
export async function cacheRetriedInboundMessage(client: QueryClient, message: InboundMessage) {
  const detailUrl = `/api/inbound-messages/${encodeURIComponent(message.id)}`
  const matchesMessageQuery = (query: { queryKey: readonly unknown[] }) => {
    const [scope, url] = query.queryKey
    return (
      scope === 'boombox-api' &&
      typeof url === 'string' &&
      (url === detailUrl || url === '/api/inbound-messages' || url.startsWith('/api/inbound-messages?'))
    )
  }
  await client.cancelQueries({ predicate: matchesMessageQuery })
  client.setQueryData(['boombox-api', detailUrl], message)
  client.setQueriesData<InboundMessage[]>(
    {
      predicate: (query) => matchesMessageQuery(query) && query.queryKey[1] !== detailUrl,
    },
    (rows) => rows?.map((row) => (row.id === message.id ? message : row)),
  )
}
